import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom"; // [NEW] Portal for Modal Layering
import { X, User, Church, LayoutDashboard, Lock, Trash2, AlertCircle, Copy, Users, Check, Shield, DollarSign, ToggleLeft, ToggleRight, Trophy, Building2 } from "lucide-react";
import AppInput from "./ui/AppInput";
import { auth, db, appId } from "../firebase"; // Import Firebase
import { updatePassword, EmailAuthProvider, reauthenticateWithCredential, signOut } from "firebase/auth";
import { doc, updateDoc, serverTimestamp, setDoc, query, where, collection, getDocs } from "firebase/firestore";
import BadgeCollection from "./BadgeCollection"; // [NEW] 배지 컬렉션
import { useModal } from "../contexts/ModalContext"; // [NEW] 커스텀 모달
import { fetchTeamMembersByOrganization, toggleMemberFinancePermission } from "../services/profileService";
import { createOrganizationFromProfile, joinOrganizationFromInviteCodeForProfile } from "../services/organizationService";
import { FONT_SCALE_OPTIONS, applyFontScale, getSavedFontScale, saveFontScale } from "../utils/fontScale";
import {
  DEFAULT_THEME,
  DEFAULT_THEME_KEY,
  THEME_PRESET_OPTIONS,
  normalizeThemeKey,
  resolveThemeColorClass,
  isThemeContrastAccessible
} from "../constants/theme";

const PRIMARY_SOFT_BORDER_CLASS_BY_BORDER = {
  "border-[#354F38]": "border-[#354F38]/20",
  "border-indigo-600": "border-indigo-600/20",
  "border-teal-700": "border-teal-700/20",
  "border-emerald-700": "border-emerald-700/20",
  "border-amber-700": "border-amber-700/20",
  "border-rose-700": "border-rose-700/20",
  "border-slate-600": "border-slate-600/20"
};

const PRIMARY_HOVER_BORDER_CLASS_BY_BORDER = {
  "border-[#354F38]": "hover:border-[#354F38]",
  "border-indigo-600": "hover:border-indigo-600",
  "border-teal-700": "hover:border-teal-700",
  "border-emerald-700": "hover:border-emerald-700",
  "border-amber-700": "hover:border-amber-700",
  "border-rose-700": "hover:border-rose-700",
  "border-slate-600": "hover:border-slate-600"
};

const PRIMARY_FOCUS_RING_SOFT_CLASS_BY_BG = {
  "bg-[#354F38]": "focus:ring-[#354F38]/20",
  "bg-indigo-600": "focus:ring-indigo-600/20",
  "bg-teal-700": "focus:ring-teal-700/20",
  "bg-emerald-700": "focus:ring-emerald-700/20",
  "bg-amber-700": "focus:ring-amber-700/20",
  "bg-rose-700": "focus:ring-rose-700/20",
  "bg-slate-600": "focus:ring-slate-600/20"
};

export default function ProfileEditModal({ isOpen, onClose, userProfile, onUpdate, myDepartments = [], onUpdateDepartments, theme = DEFAULT_THEME, earnedBadges = [], totalPoints = 0, level = 1, levelProgress = 0 }) {
  const { openAlert } = useModal();
  const [formData, setFormData] = useState({
    name: "",
    role: "전도사",
    churchName: "",
    deptName: "",
    deptThemeKey: DEFAULT_THEME_KEY,
    departmentThemeKeys: {}
  });

  const [localDepartments, setLocalDepartments] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [activeColorTab, setActiveColorTab] = useState("");

  // [NEW] Account Management State
  const [mode, setMode] = useState("profile"); // 'profile', 'password', 'withdraw', 'invite', 'join', 'leave', 'team', 'create'
  const [authError, setAuthError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null); // [NEW] For copy feedback
  const [joinCode, setJoinCode] = useState(""); // [NEW] For joining organization
  const [selectedDeptForCode, setSelectedDeptForCode] = useState(""); // [NEW] For dept code view
  const [teamMembers, setTeamMembers] = useState([]); // [NEW] For team management
  const [fontScale, setFontScale] = useState(getSavedFontScale());
  const primaryTextClass = theme.text;
  const primaryBgClass = theme.bg;
  const primaryBorderClass = theme.border;
  const primarySoftBorderClass = PRIMARY_SOFT_BORDER_CLASS_BY_BORDER[theme.border] || "border-gray-200";
  const primaryHoverBorderClass = PRIMARY_HOVER_BORDER_CLASS_BY_BORDER[theme.border] || "hover:border-gray-300";
  const primaryLightBgClass = theme.lightBg;
  const primaryFocusRingSoftClass = PRIMARY_FOCUS_RING_SOFT_CLASS_BY_BG[theme.bg] || "focus:ring-gray-300";
  const primaryHoverBgClass = theme.bg === "bg-[#354F38]" ? "hover:bg-[#2C3E30]" : "hover:opacity-90";

  // Password Change State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // [NEW] Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // [NEW] Drag State
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // [NEW] 스와이프 상태 추가
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 80;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onSwipeEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isRightSwipe = distance < -minSwipeDistance;

    // 비밀번호, 회원탈퇴 화면에서 스와이프로 profile 화면으로 돌아가기
    if (isRightSwipe && (mode === 'password' || mode === 'withdraw')) {
      setMode('profile');
      setAuthError('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    }
  };

  useEffect(() => {
    if (isOpen && userProfile) {
      setFormData({
        name: userProfile.name || "",
        role: userProfile.role || "",
        churchName: userProfile.churchName || "",
        deptName: userProfile.deptName || "",
        deptThemeKey: normalizeThemeKey(userProfile.deptThemeKey || userProfile.deptColor),
        departmentThemeKeys: Object.fromEntries(
          Object.entries(
            userProfile.departmentThemeKeys ||
            userProfile.departmentColors ||
            (userProfile.deptName ? { [userProfile.deptName]: userProfile.deptColor } : {})
          ).map(([dept, colorOrKey]) => [dept, normalizeThemeKey(colorOrKey)])
        )
      });
      setLocalDepartments(myDepartments || []);
      setActiveColorTab(userProfile.deptName || "");

      // Reset State
      setMode("profile");
      setAuthError("");
      setSuccessMsg("");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSelectedDeptForCode(userProfile.deptName || ""); // [NEW] Set default dept
      setFontScale(getSavedFontScale());

      // Reset Position
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
    }
  }, [isOpen, userProfile, myDepartments]);

  const handleFontScaleChange = (nextScale) => {
    const normalized = saveFontScale(nextScale);
    applyFontScale(normalized);
    setFontScale(normalized);
  };

  const handleColorChange = (dept, themeKey) => {
    if (!dept) return;
    if (!isThemeContrastAccessible(themeKey)) {
      openAlert("선택한 색상은 대비 기준(4.5:1)을 충족하지 못해 적용할 수 없습니다.", "접근성 기준");
      return;
    }

    setFormData(prev => {
      const newThemeKeys = { ...prev.departmentThemeKeys, [dept]: themeKey };
      return {
        ...prev,
        deptThemeKey: dept === prev.deptName ? themeKey : prev.deptThemeKey,
        departmentThemeKeys: newThemeKeys
      };
    });
  };

  // [NEW] Profile Submit Handler
  const handleSubmit = (e) => {
    e.preventDefault();

    const normalizedThemeKeys = Object.fromEntries(
      Object.entries(formData.departmentThemeKeys || {}).map(([dept, colorOrKey]) => [dept, normalizeThemeKey(colorOrKey)])
    );
    const resolvedDeptThemeKey = normalizeThemeKey(
      normalizedThemeKeys[formData.deptName] || formData.deptThemeKey
    );
    if (!isThemeContrastAccessible(resolvedDeptThemeKey)) {
      openAlert("기본 테마 색상이 대비 기준(4.5:1)을 충족하지 못합니다.", "접근성 기준");
      return;
    }

    const legacyDepartmentColors = Object.fromEntries(
      Object.entries(normalizedThemeKeys).map(([dept, themeKey]) => [dept, resolveThemeColorClass(themeKey)])
    );
    const resolvedDeptColorClass = resolveThemeColorClass(resolvedDeptThemeKey);

    // Update profile
    if (onUpdate) {
      onUpdate({
        name: formData.name,
        role: formData.role,
        churchName: formData.churchName,
        deptName: formData.deptName,
        deptThemeKey: resolvedDeptThemeKey,
        deptColor: resolvedDeptColorClass,
        departmentThemeKeys: normalizedThemeKeys,
        departmentColors: legacyDepartmentColors
      });
    }

    // Update departments
    if (onUpdateDepartments) {
      onUpdateDepartments(localDepartments);
    }

    onClose();
  };

  // [NEW] Generate invite code for a specific department
  const handleGenerateDeptCode = async (deptName) => {
    if (!auth.currentUser || !userProfile?.organizationId) return;

    setLoading(true);
    try {
      // Generate unique codes
      const generateCode = (prefix) => {
        const random = Math.random().toString(36).substring(2, 6).toUpperCase();
        const random2 = Math.random().toString(36).substring(2, 6).toUpperCase();
        return `${random}-${prefix}-${random2}`;
      };

      const adminCode = generateCode("ADMIN");
      const teacherCode = generateCode("TEACH");

      // Save to inviteCodes collection
      await setDoc(doc(db, "inviteCodes", adminCode), {
        code: adminCode,
        organizationId: userProfile.organizationId,
        departmentId: deptName,
        role: "admin",
        createdBy: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        usedCount: 0,
        maxUses: 10,
        expiresAt: null
      });

      await setDoc(doc(db, "inviteCodes", teacherCode), {
        code: teacherCode,
        organizationId: userProfile.organizationId,
        departmentId: deptName,
        role: "teacher",
        createdBy: auth.currentUser.uid,
        createdAt: serverTimestamp(),
        usedCount: 0,
        maxUses: 50,
        expiresAt: null
      });

      // Update user profile with new department codes
      const newDeptCodes = {
        ...userProfile.departmentCodes,
        [deptName]: { admin: adminCode, teacher: teacherCode }
      };

      await updateDoc(doc(db, "users", auth.currentUser.uid), {
        departmentCodes: newDeptCodes
      });

      // Update local state via onUpdate
      if (onUpdate) {
        onUpdate({ departmentCodes: newDeptCodes });
      }

      openAlert(`동역자를 위한 '${deptName}' 부서 초대장이 준비되었습니다! 💌`, "초대 코드 생성 완료");
    } catch (error) {
      console.error("Code generation failed:", error);
      openAlert("코드 생성 실패: " + error.message, "오류");
    } finally {
      setLoading(false);
    }
  };

  // [NEW] Fetch team members for admin
  const fetchTeamMembers = async () => {
    if (!userProfile?.organizationId || userProfile?.memberRole !== 'admin') {
      console.log("Team fetch skipped: ", { orgId: userProfile?.organizationId, role: userProfile?.memberRole });
      return;
    }
    setLoading(true);
    try {
      console.log("Fetching team members for org:", userProfile.organizationId);
      const members = await fetchTeamMembersByOrganization({
        db,
        organizationId: userProfile.organizationId,
        currentUserUid: auth.currentUser?.uid,
      });

      console.log(`Found ${members.length} team members`);
      setTeamMembers(members);
    } catch (error) {
      console.error("Failed to fetch team members:", error);
    } finally {
      setLoading(false);
    }
  };

  // [NEW] Toggle finance permission for a team member
  const toggleFinancePermission = async (memberUid, currentValue) => {
    try {
      const nextValue = await toggleMemberFinancePermission({
        db,
        memberUid,
        currentValue,
      });
      // Update local state
      setTeamMembers(prev => prev.map(m =>
        m.uid === memberUid ? { ...m, canManageFinance: nextValue } : m
      ));
    } catch (error) {
      console.error("Failed to update permission:", error);
      openAlert("권한 변경 실패: " + error.message, "오류");
    }
  };

  const handleJoinOrganization = async () => {
    if (!joinCode.trim()) {
      setAuthError("초대 코드를 입력해주세요.");
      return;
    }

    setLoading(true);
    setAuthError("");

    try {
      const user = auth.currentUser;
      await joinOrganizationFromInviteCodeForProfile({
        db,
        userUid: user.uid,
        code: joinCode,
      });

      setSuccessMsg("조직에 참여했습니다! 페이지를 새로고침합니다...");
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("Join organization failed:", err);
      setAuthError("참여에 실패했습니다: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrganization = async () => {
    if (!formData.churchName?.trim() || !formData.deptName?.trim()) {
      setAuthError("교회명과 부서명을 입력해주세요.");
      return;
    }

    setLoading(true);
    setAuthError("");

    try {
      const user = auth.currentUser;
      await createOrganizationFromProfile({
        db,
        appId,
        userUid: user.uid,
        churchName: formData.churchName,
        deptName: formData.deptName,
      });

      setSuccessMsg("조직이 생성되었습니다! 페이지를 새로고침합니다...");
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      console.error("Create organization failed:", err);
      setAuthError("조직 생성 실패: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape" && isOpen) onClose(); };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // [NEW] Drag Handlers
  const handleDragStart = (e) => {
    // 터치 이벤트와 마우스 이벤트 모두 지원
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;

    setIsDragging(true);
    setDragStart({
      x: clientX - position.x,
      y: clientY - position.y
    });
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;

    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;

    setPosition({
      x: clientX - dragStart.x,
      y: clientY - dragStart.y
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // 드래그 이벤트 리스너 등록
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleMouseMove);
      window.addEventListener('touchend', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleMouseMove);
        window.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  if (!isOpen) return null;

  const handleAddDept = (e) => {
    e.preventDefault();
    if (newDept.trim() && !localDepartments.includes(newDept.trim())) {
      setLocalDepartments([...localDepartments, newDept.trim()]);
      setNewDept("");
    }
  };

  const handleRemoveDept = (deptToRemove) => {
    setLocalDepartments(localDepartments.filter(d => d !== deptToRemove));
  };

  // [NEW] Password Change Logic
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setAuthError("");
    setSuccessMsg("");
    setLoading(true);

    if (userProfile?.provider && userProfile.provider !== 'email') {
      setAuthError("소셜 로그인(카카오/네이버/구글) 사용자는 비밀번호를 변경할 수 없습니다.");
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setAuthError("새 비밀번호가 일치하지 않습니다.");
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setAuthError("비밀번호는 6자 이상이어야 합니다.");
      setLoading(false);
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("로그인이 필요합니다.");

      // Re-authentication
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);

      // Update Password
      await updatePassword(user, newPassword);
      setSuccessMsg("비밀번호가 성공적으로 변경되었습니다.");
      setTimeout(() => {
        setMode("profile");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setSuccessMsg("");
      }, 1500);

    } catch (error) {
      console.error("Password change failed", error);
      if (error.code === 'auth/wrong-password') {
        setAuthError("현재 비밀번호가 올바르지 않습니다.");
      } else {
        setAuthError("비밀번호 변경 실패: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // [MODIFIED] Withdraw Logic - 조직 멤버 수에 따라 삭제 범위 결정
  const handleWithdraw = async (e) => {
    e.preventDefault();
    setAuthError("");
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("로그인이 필요합니다.");

      const { collection, query, where, getDocs, deleteDoc, writeBatch } = await import('firebase/firestore');
      const { db, appId } = await import('../firebase');

      const uid = user.uid;
      const organizationId = userProfile?.organizationId;
      const batch = writeBatch(db);
      let deletedCount = 0;

      // [NEW] 조직에 다른 멤버가 있는지 확인
      let isOnlyMember = true;
      if (organizationId) {
        const membersQuery = query(
          collection(db, "users"),
          where("organizationId", "==", organizationId)
        );
        const membersSnapshot = await getDocs(membersQuery);
        const memberCount = membersSnapshot.docs.length;
        isOnlyMember = memberCount <= 1;
        console.log(`조직 멤버 수: ${memberCount}, 본인만 있음: ${isOnlyMember}`);
      }

      // [CASE 1] 조직에 본인만 있거나, 조직이 없는 경우 → 모든 데이터 삭제
      if (isOnlyMember) {
        console.log("🗑️ 조직에 본인만 있으므로 모든 데이터 삭제");

        // 1. Delete all user-related data from collections (기존 로직)
        const collectionsToDelete = [
          "students", "teachers", "schedules", "finances",
          "visitLogs", "counselingLogs", "meetingMinutes"
        ];

        for (const colName of collectionsToDelete) {
          try {
            // organizationId가 있으면 조직 데이터도 삭제
            const q = organizationId
              ? query(collection(db, colName), where("organizationId", "==", organizationId))
              : query(collection(db, colName), where("userId", "==", uid));
            const snapshot = await getDocs(q);
            snapshot.docs.forEach((docSnap) => {
              batch.delete(doc(db, colName, docSnap.id));
              deletedCount++;
            });
          } catch (e) {
            console.log(`Collection ${colName} delete skipped:`, e.message);
          }
        }

        // 2. Delete attendance data (nested path)
        try {
          const attendanceRef = collection(db, "artifacts", appId, "users", uid, "attendance");
          const attendanceSnapshot = await getDocs(attendanceRef);
          attendanceSnapshot.docs.forEach((docSnap) => {
            batch.delete(doc(db, "artifacts", appId, "users", uid, "attendance", docSnap.id));
            deletedCount++;
          });
        } catch (e) {
          console.log("Attendance delete skipped:", e.message);
        }

        // 3. Delete invite codes created by this user
        if (organizationId) {
          try {
            const codesQuery = query(
              collection(db, "inviteCodes"),
              where("organizationId", "==", organizationId)
            );
            const codesSnapshot = await getDocs(codesQuery);
            codesSnapshot.docs.forEach((docSnap) => {
              batch.delete(doc(db, "inviteCodes", docSnap.id));
              deletedCount++;
            });
            console.log("✅ 조직 초대 코드 삭제 완료");
          } catch (e) {
            console.log("Invite codes delete skipped:", e.message);
          }
        }

      } else {
        // [CASE 2] 조직에 다른 멤버도 있는 경우 → 개인 데이터만 삭제, 조직 공유 데이터는 유지
        console.log("✨ 조직에 다른 멤버가 있으므로 개인 데이터만 삭제 (조직 공유 데이터 유지)");
      }

      // 4. Delete user preferences (개인 데이터 - 항상 삭제)
      try {
        const prefsRef = collection(db, "userPreferences");
        const prefsQ = query(prefsRef, where("userId", "==", uid));
        const prefsSnapshot = await getDocs(prefsQ);
        prefsSnapshot.docs.forEach((docSnap) => {
          batch.delete(doc(db, "userPreferences", docSnap.id));
          deletedCount++;
        });
      } catch (e) {
        console.log("Preferences delete skipped:", e.message);
      }

      // 5. Delete user achievements (개인 데이터 - 항상 삭제)
      try {
        await deleteDoc(doc(db, "userAchievements", uid));
        deletedCount++;
        console.log("✅ 업적 데이터 삭제 완료");
      } catch (e) {
        console.log("Achievements delete skipped:", e.message);
      }

      // 6. Commit batch delete
      if (deletedCount > 0) {
        await batch.commit();
        console.log(`✅ ${deletedCount}개의 데이터 삭제 완료`);
      }

      // 7. Delete user profile document (항상 삭제)
      await deleteDoc(doc(db, "users", uid));
      console.log("✅ 사용자 프로필 삭제 완료");

      // 8. Delete Firebase Auth account
      try {
        await user.delete();
        console.log("✅ Firebase Auth 계정 삭제 완료");
      } catch (authError) {
        // If auth deletion fails (requires recent login), just sign out
        console.log("Auth 계정 삭제 실패 (재로그인 필요):", authError.message);
        await signOut(auth);
      }

      // 9. Clear local storage
      localStorage.clear();
      sessionStorage.clear();

      // Reload to clear app state
      window.location.href = "/";

    } catch (error) {
      console.error("Withdrawal failed", error);
      setAuthError("회원 탈퇴 실패: " + error.message);
    } finally {
      setLoading(false);
    }
  };


  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center z-[10000] md:p-4 pointer-events-none">
      <div
        className="bg-white md:rounded-3xl w-full h-full md:h-auto md:max-h-[90vh] md:max-w-sm shadow-2xl relative overflow-hidden flex flex-col pointer-events-auto"
        style={{
          transform: isMobile ? undefined : `translate(${position.x}px, ${position.y}px)`,
          cursor: !isMobile && isDragging ? 'grabbing' : 'default'
        }}
      >

        {/* Header - Sticky on Mobile, Draggable on Desktop */}
        <div
          className="sticky top-0 bg-[#F9F9F8] z-10 px-6 pt-[max(env(safe-area-inset-top),45px)] pb-4 border-b border-gray-200 flex items-center shrink-0 md:rounded-t-3xl"
          onMouseDown={!isMobile ? handleDragStart : undefined}
          style={{ cursor: !isMobile && isDragging ? 'grabbing' : (!isMobile ? 'grab' : 'default') }}
        >
          {/* Left Spacer */}
          <div className="w-10"></div>

          {/* Center Title */}
          <div className="flex-1 flex justify-center">
            <h3 className="text-xl font-black text-[#2C3E30] flex items-center gap-2 font-do-hyeon">
              {mode === 'profile' && <><img src="/images/Bust_in_silhouette.png" alt="" className="w-6 h-6" /> 내 정보 수정</>}
              {mode === 'badges' && <><img src="/images/Trophy.png" alt="" className="w-6 h-6" /> 사역 여정</>}
              {mode === 'password' && <><Lock className={primaryTextClass} /> 비밀번호 변경</>}
              {mode === 'withdraw' && <><img src="/images/Wastebasket.png" alt="" className="w-6 h-6" /> TheSTAFF와 작별하기</>}
              {mode === 'invite' && <><Users className={primaryTextClass} /> 함께할 동역자 초대</>}
              {mode === 'join' && <><Users className={primaryTextClass} /> 공동체에 합류하기</>}
              {mode === 'leave' && <><AlertCircle className="text-amber-500" /> 공동체와 작별하기</>}
              {mode === 'team' && <><img src="/images/Shield.png" alt="" className="w-6 h-6" /> 함께 일하는 동역자들</>}
              {mode === 'create' && <><Building2 className="text-emerald-500" /> 새 조직 생성</>}
            </h3>
          </div>

          {/* Right Button */}
          <div className="w-10 flex justify-end">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 -mr-2"><X size={24} /></button>
          </div>
        </div>

        <div className="p-6 pt-4 flex-1 overflow-y-auto custom-scrollbar overscroll-contain">
          {/* --- MODE: PROFILE --- */}
          {mode === 'profile' && (
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* 0. 🏆 사역 여정 (최상단 배치) */}
              <button
                type="button"
                onClick={() => setMode('badges')}
                className="w-full p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl border border-amber-200 hover:border-amber-300 transition-all group shadow-sm mb-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/20">
                      <span className="text-xl font-black text-white">Lv.{level}</span>
                    </div>
                    <div className="text-left">
                      <p className="font-bold text-gray-800 flex items-center gap-1">
                        <img src="/images/Trophy.png" alt="" className="w-5 h-5" />
                        사역 여정
                      </p>
                      <p className="text-xs text-gray-500 flex items-center gap-1">
                        {earnedBadges.length}개 배지 •
                        <img src="/images/Star.png" alt="" className="w-3 h-3" />
                        {totalPoints.toLocaleString()}P
                      </p>
                    </div>
                  </div>
                  <img src="/images/Trophy.png" alt="" className="w-8 h-8 group-hover:scale-110 transition-transform" />
                </div>
              </button>
              {/* [NEW] 이메일(아이디) 표시 - Read Only */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 ml-1 flex items-center gap-1">
                  계정(아이디)
                  <span className="text-[10px] text-gray-400 font-normal bg-gray-100 px-1.5 py-0.5 rounded-lg">수정 불가</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <User size={16} />
                  </div>
                  <input
                    className="w-full border p-3 pl-10 rounded-xl bg-gray-100/50 text-gray-500 font-medium cursor-default focus:outline-none"
                    value={userProfile?.email || auth.currentUser?.email || "이메일 정보 없음"}
                    readOnly
                  />
                  {userProfile?.provider && userProfile.provider !== 'email' && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 bg-white px-2 py-1 rounded-lg border border-gray-100 shadow-sm">
                      {userProfile.provider === 'naver' && <span className="text-[10px] font-bold text-[#03C75A]">NAVER</span>}
                      {userProfile.provider === 'kakao' && <span className="text-[10px] font-bold text-[#FEE500] bg-black/5 px-1 rounded">KAKAO</span>}
                      {userProfile.provider === 'google' && <span className="text-[10px] font-bold text-blue-500">GOOGLE</span>}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2">
                <div className="flex-1 space-y-1">
                  <label className="text-xs font-bold text-gray-500 ml-1">성함</label>
                  <AppInput 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                    onClear={() => setFormData({ ...formData, name: "" })}
                    required 
                  />
                </div>
                <div className="w-1/3 space-y-1">
                  <label className="text-xs font-bold text-gray-500 ml-1">직분</label>
                  <AppInput 
                    value={formData.role} 
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })} 
                    onClear={() => setFormData({ ...formData, role: "" })}
                    required 
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 ml-1">글자 크기</label>
                <div className="grid grid-cols-5 gap-1.5 p-2 rounded-2xl border border-gray-100 bg-[#F9F9F8]">
                  {FONT_SCALE_OPTIONS.map((option) => {
                    const isSelected = fontScale === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleFontScaleChange(option.value)}
                        className={`px-1 py-2.5 rounded-xl text-[11px] font-bold transition border leading-tight ${isSelected
                          ? `${primaryBgClass} text-white ${primaryBorderClass} shadow-sm`
                          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-400 ml-1">앱 전체 글자 크기에 바로 적용됩니다.</p>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 ml-1 flex justify-between items-center">
                  교회명
                  {userProfile?.memberRole !== 'admin' && (
                    <span className="text-[10px] text-amber-600 flex items-center gap-0.5">
                      <Lock size={10} /> 관리자 설정
                    </span>
                  )}
                </label>
                <div className="relative">
                  <img src="/images/Church.png" alt="" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
                  <input
                    className={`w-full border p-3 pl-10 rounded-xl transition ${userProfile?.memberRole !== 'admin' ? 'bg-gray-100/50 text-gray-400 cursor-not-allowed border-gray-100' : `bg-[#F9F9F8] focus:bg-white focus:ring-2 ${primaryFocusRingSoftClass} outline-none`}`}
                    value={formData.churchName}
                    onChange={(e) => setFormData({ ...formData, churchName: e.target.value })}
                    readOnly={userProfile?.memberRole !== 'admin'}
                    required
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 ml-1 flex justify-between items-center">
                  대표 부서명
                  {userProfile?.memberRole !== 'admin' && (
                    <span className="text-[10px] text-amber-600 flex items-center gap-0.5">
                      <Lock size={10} /> 관리자 설정
                    </span>
                  )}
                </label>
                <div className="relative">
                  <img src="/images/Card_index.png" alt="" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
                  <input
                    className={`w-full border p-3 pl-10 rounded-xl transition ${userProfile?.memberRole !== 'admin' ? 'bg-gray-100/50 text-gray-400 cursor-not-allowed border-gray-100' : `bg-[#F9F9F8] focus:bg-white focus:ring-2 ${primaryFocusRingSoftClass} outline-none`}`}
                    value={formData.deptName}
                    onChange={(e) => setFormData({ ...formData, deptName: e.target.value })}
                    readOnly={userProfile?.memberRole !== 'admin'}
                    required
                  />
                </div>
              </div>

              <div id="tour-dept-color-section" className="space-y-2 animate-fade-in pt-4 border-t border-gray-100">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-black text-[#2C3E30] ml-1 flex items-center gap-1">
                    <img src="/images/Artist_palette.png" alt="" className="w-4 h-4" /> 나의 테마 색상
                  </label>
                  <span className="text-[10px] text-gray-400 font-medium">부서별 개별 설정</span>
                </div>
                <div id="tour-dept-tabs" className="flex gap-2 overflow-x-auto custom-scrollbar pb-2">
                  {[formData.deptName, ...localDepartments].filter(Boolean).map((dept) => (
                    <button
                      key={dept}
                      type="button"
                      onClick={() => setActiveColorTab(dept)}
                      className={`
                        px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all border
                        ${activeColorTab === dept
                          ? `${primaryBgClass} text-white ${primaryBorderClass} shadow-md`
                          : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}
                      `}
                    >
                      <span className={`w-2 h-2 rounded-full inline-block mr-1.5 ${resolveThemeColorClass(formData.departmentThemeKeys?.[dept] || formData.deptThemeKey)}`}></span>
                      {dept}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2 p-3 bg-[#F9F9F8] rounded-2xl border border-gray-100 overflow-x-auto custom-scrollbar">
                  {THEME_PRESET_OPTIONS.map((preset) => {
                    const currentDeptThemeKey = normalizeThemeKey(formData.departmentThemeKeys?.[activeColorTab] || formData.deptThemeKey);
                    const isSelected = currentDeptThemeKey === preset.key;
                    return (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() => handleColorChange(activeColorTab, preset.key)}
                        className={`
                          w-9 h-9 rounded-full flex-shrink-0 transition-all duration-300 shadow-sm border-2
                          ${isSelected ? 'border-gray-800 scale-110 ring-2 ring-gray-200' : 'border-transparent hover:scale-105'}
                          ${preset.swatchClass}
                        `}
                        title={`${preset.label} (${preset.primaryHex})`}
                      />
                    )
                  })}
                </div>
              </div>

              {userProfile?.memberRole === 'admin' && (
                <div id="tour-dept-add-section" className="pt-4 border-t border-gray-100 animate-fade-in">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-black text-[#2C3E30] ml-1 flex items-center gap-1">
                      <img src="/images/Card_index.png" alt="" className="w-4 h-4" /> 내 관리 부서 목록
                    </label>
                    <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap">* 대시보드 필터에 적용됩니다.</span>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <input
                      id="tour-dept-input"
                      className="flex-1 border p-2 rounded-xl bg-[#F9F9F8] text-sm focus:bg-white outline-none"
                      placeholder="부서 추가 (예: 찬양팀)"
                      value={newDept}
                      onChange={(e) => setNewDept(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddDept(e); } }}
                    />
                    <button id="tour-dept-add-btn" type="button" onClick={handleAddDept} className="bg-gray-100 text-gray-600 px-3 py-2 rounded-xl text-sm font-bold hover:bg-gray-200">추가</button>
                  </div>
                  <div className="flex flex-wrap gap-2 min-h-[40px] p-2 bg-gray-50 rounded-xl border border-gray-100">
                    {localDepartments.length === 0 && <span className="text-xs text-gray-400 p-1">추가된 부서가 없습니다.</span>}
                    {localDepartments.map((dept, idx) => (
                      <span key={idx} className={`bg-white ${primaryTextClass} ${primarySoftBorderClass} text-xs font-bold px-3 py-1.5 rounded-lg flex items-center gap-2 shadow-sm`}>
                        {dept}
                        <button type="button" onClick={() => handleRemoveDept(dept)} className="text-gray-400 hover:text-red-500 transition-colors bg-gray-100 rounded-full p-0.5"><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 ml-1">* 여기서 추가한 부서는 상단 테마 설정 및 대시보드 필터에 나타납니다.</p>
                </div>
              )}


              <button className={`w-full ${primaryBgClass} text-white py-4 rounded-2xl font-bold ${primaryHoverBgClass} transition shadow-lg ${theme.shadow} mt-4 mb-safe`}>
                수정 완료
              </button>

              {/* Account Settings Links - Unified Grid Layout */}
              <div className="pt-8 border-t border-gray-100 space-y-4 px-1 pb-safe animate-fade-in">
                <div className="space-y-2">
                  <h4 className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider ml-1">계정 및 조직 관리</h4>
                  <div className="grid grid-cols-2 gap-2">

                    {/* [Group A] Organization Actions */}
                    {userProfile?.memberRole === 'admin' && userProfile?.organizationId && (
                      <>
                        <button type="button" onClick={() => setMode('invite')} className={`bg-gray-50 hover:bg-white border border-gray-200 ${primaryHoverBorderClass} p-3 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all hover:shadow-md group`}>
                          <div className="bg-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                            <Users size={18} className={primaryTextClass} />
                          </div>
                          <span className="text-xs font-bold text-gray-600">초대 코드</span>
                        </button>
                        <button type="button" onClick={() => { setMode('team'); fetchTeamMembers(); }} className="bg-gray-50 hover:bg-white border border-gray-200 hover:border-indigo-500 p-3 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all hover:shadow-md group">
                          <div className="bg-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                            <img src="/images/Shield.png" alt="" className="w-4 h-4" />
                          </div>
                          <span className="text-xs font-bold text-gray-600">권한 관리</span>
                        </button>
                      </>
                    )}

                    {userProfile?.organizationId && (
                      <button type="button" onClick={() => setMode('leave')} className="bg-gray-50 hover:bg-white border border-gray-200 hover:border-amber-500 p-3 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all hover:shadow-md group">
                        <div className="bg-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                          <AlertCircle size={18} className="text-amber-500" />
                        </div>
                        <span className="text-xs font-bold text-gray-600">조직 나가기</span>
                      </button>
                    )}

                    {/* [Group B] No Organization Actions */}
                    {!userProfile?.organizationId && (
                      <>
                        <button type="button" onClick={() => setMode('join')} className="bg-blue-50 hover:bg-white border border-blue-100 hover:border-blue-500 p-3 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all hover:shadow-md group">
                          <div className="bg-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                            <Users size={18} className="text-blue-500" />
                          </div>
                          <span className="text-xs font-bold text-blue-600">조직 참여</span>
                        </button>
                        <button type="button" onClick={() => setMode('create')} className="bg-emerald-50 hover:bg-white border border-emerald-100 hover:border-emerald-500 p-3 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all hover:shadow-md group">
                          <div className="bg-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                            <Building2 size={18} className="text-emerald-500" />
                          </div>
                          <span className="text-xs font-bold text-emerald-600">새 조직 생성</span>
                        </button>
                      </>
                    )}

                    {/* [Group C] Common Actions */}
                    <button type="button" onClick={() => setMode('withdraw')} className="bg-rose-50 hover:bg-white border border-rose-100 hover:border-rose-300 p-3 rounded-2xl flex flex-col items-center justify-center gap-1.5 transition-all hover:shadow-md group">
                      <div className="bg-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                        <img src="/images/Wastebasket.png" alt="" className="w-4 h-4" />
                      </div>
                      <span className="text-xs font-bold text-rose-500">회원 탈퇴</span>
                    </button>

                  </div>
                </div>
              </div>
            </form>
          )}

          {/* --- MODE: BADGES --- */}
          {mode === 'badges' && (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setMode('profile')}
                className="text-sm text-gray-400 hover:text-gray-600 mb-2"
              >
                ← 돌아가기
              </button>

              <BadgeCollection
                earnedBadges={earnedBadges}
                totalPoints={totalPoints}
                level={level}
                levelProgress={levelProgress}
              />
            </div>
          )}

          {/* --- MODE: PASSWORD --- */}
          {mode === 'password' && (
            <form
              onSubmit={handleChangePassword}
              className="space-y-4"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onSwipeEnd}
            >
              {userProfile?.provider && userProfile.provider !== 'email' ? (
                <div className="text-center py-10 space-y-4">
                  <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                    <Lock className="text-gray-400" size={32} />
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-bold text-gray-800">비밀번호 변경 불가</h4>
                    <p className="text-sm text-gray-500 break-keep">
                      소셜 로그인({userProfile.provider === 'kakao' ? '카카오' : userProfile.provider === 'naver' ? '네이버' : userProfile.provider}) 계정은<br />해당 서비스에서 비밀번호를 관리합니다.
                    </p>
                  </div>
                  <button type="button" onClick={() => setMode('profile')} className={`mt-4 px-6 py-3 ${primaryBgClass} text-white rounded-xl font-bold text-sm ${primaryHoverBgClass}`}>
                    확인
                  </button>
                </div>
              ) : (
                <>
                  <div className="bg-gray-50 p-4 rounded-xl text-xs text-gray-600 mb-2">
                    안전한 계정 보호를 위해 주기적으로 비밀번호를 변경해 주세요.
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 ml-1">현재 비밀번호</label>
                    <input type="password" className="w-full border p-3 rounded-xl bg-[#F9F9F8] focus:bg-white outline-none"
                      value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 ml-1">새 비밀번호</label>
                    <input type="password" className="w-full border p-3 rounded-xl bg-[#F9F9F8] focus:bg-white outline-none"
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required placeholder="6자 이상 입력" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 ml-1">새 비밀번호 확인</label>
                    <input type="password" className="w-full border p-3 rounded-xl bg-[#F9F9F8] focus:bg-white outline-none"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                  </div>

                  {authError && <div className="text-xs text-rose-500 font-bold flex items-center gap-1"><AlertCircle size={12} /> {authError}</div>}
                  {successMsg && <div className="text-xs text-emerald-600 font-bold flex items-center gap-1"><Lock size={12} /> {successMsg}</div>}

                  <div className="pt-4 space-y-3">
                    <button disabled={loading} className={`w-full ${primaryBgClass} text-white py-4 rounded-2xl font-bold ${primaryHoverBgClass} transition shadow-lg ${theme.shadow} disabled:opacity-50`}>
                      {loading ? "처리 중..." : "비밀번호 변경하기"}
                    </button>
                    <button type="button" onClick={() => setMode('profile')} className="w-full py-3 text-gray-400 font-bold text-xs hover:text-gray-600">
                      취소하고 돌아가기
                    </button>
                  </div>
                </>
              )}
            </form>
          )}

          {/* --- MODE: WITHDRAW --- */}
          {mode === 'withdraw' && (
            <form
              onSubmit={handleWithdraw}
              className="space-y-4"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onSwipeEnd}
            >
              <div className="bg-rose-50 p-4 rounded-xl text-xs text-rose-700 leading-relaxed mb-4 border border-rose-100">
                <span className="font-bold block mb-1">⚠️ 잠시만요! 정말 떠나시는 건가요?</span>
                탈퇴하시면 모든 데이터가 정리되어 다시 만나기 어려워질 수 있어요.<br />
                <b>이 작업은 되돌릴 수 없으며, 로그인이 즉시 불가능해집니다. 신중히 결정해 주세요. 😥</b>
              </div>

              {/* [MOD] Always Social-Style Withdraw (No Password) */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 ml-1">탈퇴 확인</label>
                <div className="p-3 bg-gray-50 rounded-xl text-sm font-medium text-gray-600">
                  <span className="text-rose-500 font-bold">정말 탈퇴하시겠습니까?</span><br />
                  탈퇴 버튼을 누르면 즉시 처리됩니다.
                </div>
              </div>
              <div className="pt-4 space-y-3">
                <button disabled={loading} className="w-full bg-rose-500 text-white py-4 rounded-2xl font-bold hover:bg-rose-600 transition shadow-lg shadow-rose-500/20 disabled:opacity-50">
                  {loading ? "처리 중..." : "네, 아쉽지만 떠날게요"}
                </button>
                <button type="button" onClick={() => setMode('profile')} className="w-full py-3 text-gray-400 font-bold text-xs hover:text-gray-600">
                  조금 더 함께할게요 (취소)
                </button>
              </div>
            </form>
          )}

          {/* --- MODE: INVITE --- */}
          {mode === 'invite' && (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setMode('profile')}
                className="text-sm text-gray-400 hover:text-gray-600 mb-2"
              >
                ← 돌아가기
              </button>

              <div className={`${primaryLightBgClass} p-4 rounded-xl text-xs text-gray-600 mb-4`}>
                💡 같은 부서를 섬길 선생님들께 이 코드를 전달해 주세요. 소중한 동역자의 합류를 기다립니다! ✨
              </div>

              {/* Department Tabs */}
              <div className="flex gap-2 flex-wrap mb-4">
                {[userProfile?.deptName, ...localDepartments].filter((d, i, arr) => d && arr.indexOf(d) === i).map(dept => (
                  <button
                    key={dept}
                    type="button"
                    onClick={() => setSelectedDeptForCode(dept)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${selectedDeptForCode === dept
                      ? `${primaryBgClass} text-white`
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                  >
                    {dept}
                  </button>
                ))}
              </div>

              {selectedDeptForCode && (
                <>
                  {/* Department Code Display */}
                  {userProfile?.departmentCodes?.[selectedDeptForCode] ? (
                    <>
                      {/* Admin Code for this dept */}
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 ml-1 flex items-center gap-2">
                          👑 {selectedDeptForCode} 관리자 코드
                        </label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 p-3 bg-gray-100 rounded-xl font-mono text-center text-sm tracking-wider font-bold text-gray-700">
                            {userProfile.departmentCodes[selectedDeptForCode]?.admin || '없음'}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const code = userProfile.departmentCodes[selectedDeptForCode]?.admin;
                              if (code) {
                                navigator.clipboard.writeText(code);
                                setCopiedCode(`admin-${selectedDeptForCode}`);
                                setTimeout(() => setCopiedCode(null), 2000);
                              }
                            }}
                            className={`p-3 ${primaryBgClass} text-white rounded-xl ${primaryHoverBgClass} transition`}
                          >
                            {copiedCode === `admin-${selectedDeptForCode}` ? <Check size={18} /> : <Copy size={18} />}
                          </button>
                        </div>
                      </div>

                      {/* Teacher Code for this dept */}
                      <div className="space-y-2">
                        <label className="text-xs font-bold text-gray-500 ml-1 flex items-center gap-2">
                          👨‍🏫 {selectedDeptForCode} 교사 코드
                        </label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 p-3 bg-gray-100 rounded-xl font-mono text-center text-sm tracking-wider font-bold text-gray-700">
                            {userProfile.departmentCodes[selectedDeptForCode]?.teacher || '없음'}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const code = userProfile.departmentCodes[selectedDeptForCode]?.teacher;
                              if (code) {
                                navigator.clipboard.writeText(code);
                                setCopiedCode(`teacher-${selectedDeptForCode}`);
                                setTimeout(() => setCopiedCode(null), 2000);
                              }
                            }}
                            className="p-3 bg-gray-200 text-gray-600 rounded-xl hover:bg-gray-300 transition"
                          >
                            {copiedCode === `teacher-${selectedDeptForCode}` ? <Check size={18} /> : <Copy size={18} />}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* No code for this dept - show generate button */
                    <div className="text-center py-6">
                      <p className="text-gray-500 text-sm mb-4">
                        '{selectedDeptForCode}' 부서의 초대 코드가 없습니다.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleGenerateDeptCode(selectedDeptForCode)}
                        disabled={loading}
                        className={`px-6 py-3 ${primaryBgClass} text-white rounded-xl font-bold ${primaryHoverBgClass} transition disabled:opacity-50`}
                      >
                        {loading ? '생성 중...' : `${selectedDeptForCode} 초대장 만들기`}
                      </button>
                    </div>
                  )}
                </>
              )}

              <div className="bg-amber-50 p-4 rounded-xl text-xs text-amber-700 mt-4 border border-amber-100">
                ⚠️ <b>주의:</b> 관리자 코드는 모든 권한을 가집니다. 신뢰할 수 있는 분에게만 공유하세요.
              </div>
            </div>
          )}

          {/* --- MODE: JOIN --- */}
          {mode === 'join' && (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setMode('profile')}
                className="text-sm text-gray-400 hover:text-gray-600 mb-2"
              >
                ← 돌아가기
              </button>

              <div className="bg-blue-50 p-4 rounded-xl text-xs text-gray-600 mb-4 border border-blue-100">
                💡 관리자에게 받은 초대 코드를 입력해 주세요.<br />
                함께 사역하는 동역자들의 공간으로 안내해 드릴게요. ✨
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 ml-1">초대 코드</label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-ADMIN-XXXX 또는 XXXX-TEACH-XXXX"
                  className={`w-full p-4 border border-gray-200 rounded-xl bg-[#F9F9F8] focus:bg-white focus:ring-2 ${primaryFocusRingSoftClass} outline-none transition font-mono text-center text-lg tracking-wider`}
                  maxLength={15}
                />
              </div>

              {authError && (
                <div className="flex items-center gap-2 text-rose-500 text-sm bg-rose-50 p-3 rounded-xl">
                  <AlertCircle size={16} />
                  {authError}
                </div>
              )}

              {successMsg && (
                <div className="flex items-center gap-2 text-emerald-600 text-sm bg-emerald-50 p-3 rounded-xl">
                  <Check size={16} />
                  {successMsg}
                </div>
              )}

              <button
                type="button"
                disabled={loading || joinCode.length < 10}
                onClick={handleJoinOrganization}
                className={`w-full p-4 ${primaryBgClass} text-white rounded-2xl font-bold ${primaryHoverBgClass} transition-all shadow-lg ${theme.shadow} disabled:opacity-50 flex items-center justify-center gap-2`}
              >
                {loading ? (
                  <>확인 중...</>
                ) : (
                  <>
                    <Users size={20} />
                    조직 참여하기
                  </>
                )}
              </button>
            </div>
          )}

          {/* --- MODE: LEAVE --- */}
          {mode === 'leave' && (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setMode('profile')}
                className="text-sm text-gray-400 hover:text-gray-600 mb-2"
              >
                ← 돌아가기
              </button>

              <div className="bg-amber-50 p-4 rounded-xl text-xs text-amber-700 mb-4 border border-amber-100">
                ⚠️ 이 공동체를 떠나시겠어요? 지금까지의 활동 내역은 안전하게 보관되지만, 더 이상 조직의 데이터에 접근할 수 없게 됩니다. 🥲
              </div>

              <div className="bg-gray-50 p-4 rounded-xl space-y-2">
                <div className="text-sm">
                  <span className="text-gray-500">현재 조직:</span>
                  <span className="font-bold text-gray-800 ml-2">{userProfile?.churchName || '알 수 없음'}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500">내 역할:</span>
                  <span className="font-bold text-gray-800 ml-2">
                    {userProfile?.memberRole === 'admin' ? '👑 관리자' : '👨‍🏫 교사'}
                  </span>
                </div>
              </div>

              {authError && (
                <div className="flex items-center gap-2 text-rose-500 text-sm bg-rose-50 p-3 rounded-xl">
                  <AlertCircle size={16} />
                  {authError}
                </div>
              )}

              {successMsg && (
                <div className="flex items-center gap-2 text-emerald-600 text-sm bg-emerald-50 p-3 rounded-xl">
                  <Check size={16} />
                  {successMsg}
                </div>
              )}

              <button
                type="button"
                disabled={loading}
                onClick={async () => {
                  setLoading(true);
                  setAuthError("");

                  try {
                    const { doc, updateDoc, deleteField, getDoc } = await import('firebase/firestore');
                    const { db } = await import('../firebase');

                    const user = auth.currentUser;
                    const orgId = userProfile?.organizationId;

                    // 1. Update user profile - remove organization fields
                    await updateDoc(doc(db, "users", user.uid), {
                      organizationId: deleteField(),
                      memberRole: deleteField(),
                      adminInviteCode: deleteField(),
                      teacherInviteCode: deleteField(),
                      setupComplete: false  // Reset so they can join another org
                    });

                    // 2. Decrement org member count (if org exists)
                    if (orgId) {
                      try {
                        const orgDoc = await getDoc(doc(db, "organizations", orgId));
                        if (orgDoc.exists()) {
                          const currentCount = orgDoc.data().memberCount || 1;
                          await updateDoc(doc(db, "organizations", orgId), {
                            memberCount: Math.max(0, currentCount - 1)
                          });
                        }
                      } catch (e) {
                        console.error("Failed to update org member count:", e);
                        // Non-critical, continue
                      }
                    }

                    setSuccessMsg("조직에서 나왔습니다. 페이지를 새로고침합니다...");
                    setTimeout(() => {
                      window.location.reload();
                    }, 1500);

                  } catch (err) {
                    console.error("Leave organization failed:", err);
                    setAuthError("조직 나가기 실패: " + err.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                className="w-full p-4 bg-amber-500 text-white rounded-2xl font-bold hover:bg-amber-600 transition-all shadow-lg shadow-amber-500/20 disabled:opacity-50"
              >
                {loading ? "처리 중..." : "조직 나가기"}
              </button>

              <button
                type="button"
                onClick={() => setMode('profile')}
                className="w-full py-3 text-gray-400 font-bold text-xs hover:text-gray-600"
              >
                취소
              </button>
            </div>
          )}

          {/* --- MODE: TEAM MANAGEMENT --- */}
          {mode === 'team' && (
            <div className="space-y-4">
              <div className="bg-indigo-50 p-4 rounded-xl border border-indigo-100">
                <p className="text-sm text-indigo-700 font-medium">
                  우리 팀 선생님들의 권한을 세심하게 관리할 수 있어요. 재정 권한은 신뢰할 수 있는 분에게만 살짝 열어주세요! 🛡️
                </p>
              </div>

              {loading ? (
                <div className="text-center py-8 text-gray-400">
                  <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                  불러오는 중...
                </div>
              ) : teamMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Users size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="font-bold">팀원이 없습니다</p>
                  <p className="text-xs mt-1">초대 코드로 팀원을 초대해 보세요</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {teamMembers.map(member => (
                    <div key={member.uid} className="flex items-center gap-3 p-4 bg-white rounded-2xl border border-gray-100 shadow-sm">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 font-bold text-sm">
                        {member.name?.[0] || '?'}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-gray-800 truncate">{member.name || '이름 없음'}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${member.memberRole === 'admin'
                            ? 'bg-amber-100 text-amber-600'
                            : 'bg-gray-100 text-gray-500'
                            }`}>
                            {member.memberRole === 'admin' ? '관리자' : '교사'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 truncate">{member.deptName || member.churchName || ''}</p>
                      </div>

                      {/* Finance Permission Toggle */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-xs text-gray-400 hidden sm:block">재정</span>
                        <button
                          onClick={() => toggleFinancePermission(member.uid, member.canManageFinance)}
                          className={`p-1.5 rounded-lg transition-all ${member.canManageFinance
                            ? 'bg-emerald-100 text-emerald-600'
                            : 'bg-gray-100 text-gray-400'
                            }`}
                          title={member.canManageFinance ? '재정 권한 있음' : '재정 권한 없음'}
                        >
                          {member.canManageFinance ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => setMode('profile')}
                className="w-full py-3 text-gray-400 font-bold text-xs hover:text-gray-600 mt-4"
              >
                돌아가기
              </button>
            </div>
          )}

          {/* --- MODE: CREATE ORGANIZATION --- */}
          {mode === 'create' && (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setMode('profile')}
                className="text-sm text-gray-400 hover:text-gray-600 mb-2"
              >
                ← 돌아가기
              </button>

              <div className="bg-emerald-50 p-4 rounded-xl text-xs text-emerald-700 mb-4 border border-emerald-100">
                🏛️ 새로운 교회/공동체를 등록합니다.<br />
                등록 후 관리자 권한으로 다른 선생님들을 초대할 수 있어요!
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 ml-1">교회명 *</label>
                <div className="relative">
                  <img src="/images/Church.png" alt="" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.churchName}
                    onChange={(e) => setFormData({ ...formData, churchName: e.target.value })}
                    placeholder="예: 사랑의교회"
                    className="w-full p-4 pl-10 border border-gray-200 rounded-xl bg-[#F9F9F8] focus:bg-white focus:ring-2 focus:ring-emerald-500/20 outline-none transition"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 ml-1">대표 부서명 *</label>
                <div className="relative">
                  <img src="/images/Card_index.png" alt="" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
                  <input
                    type="text"
                    value={formData.deptName}
                    onChange={(e) => setFormData({ ...formData, deptName: e.target.value })}
                    placeholder="예: 청년부, 유치부"
                    className="w-full p-4 pl-10 border border-gray-200 rounded-xl bg-[#F9F9F8] focus:bg-white focus:ring-2 focus:ring-emerald-500/20 outline-none transition"
                  />
                </div>
              </div>

              {authError && (
                <div className="flex items-center gap-2 text-rose-500 text-sm bg-rose-50 p-3 rounded-xl">
                  <AlertCircle size={16} />
                  {authError}
                </div>
              )}

              {successMsg && (
                <div className="flex items-center gap-2 text-emerald-600 text-sm bg-emerald-50 p-3 rounded-xl">
                  <Check size={16} />
                  {successMsg}
                </div>
              )}

              <button
                type="button"
                disabled={loading || !formData.churchName?.trim() || !formData.deptName?.trim()}
                onClick={handleCreateOrganization}
                className="w-full p-4 bg-emerald-500 text-white rounded-2xl font-bold hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>생성 중...</>
                ) : (
                  <>
                    <Building2 size={20} />
                    조직 생성하고 관리자 되기
                  </>
                )}
              </button>

              <p className="text-[10px] text-gray-400 text-center mt-2">
                * 생성 후 초대 코드로 다른 선생님들을 초대할 수 있습니다
              </p>
            </div>
          )}
        </div>
      </div >
    </div >,
    document.body
  );
}
